import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Package, ShoppingCart, DollarSign, Clock, AlertTriangle, Plus,
  TrendingUp, TrendingDown, ArrowRight,
  ChevronDown,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import { analyticsAPI, orderAPI, productAPI, authAPI } from '../api';
import dayjs from 'dayjs';
import AccessModule from '../components/AccessModule/AccessModule';
import ViewOrders from './Orders/ViewOrders';
import api from '../api';

const StatCard = ({
  title,
  value,
  icon: Icon,
  bg,
  trend,
  prefix = '',
  onClick,
}: {
  title: string;
  value: string | number;
  icon: any;
  bg: string;
  trend?: number;
  prefix?: string;
  onClick?: () => void;
}) => (
  <div
    className="stat-card"
    style={{ background: bg, cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <p style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.75)', marginBottom: '8px' }}>{title}</p>
        <h3 style={{ fontSize: '28px', fontWeight: '800', color: 'white' }}>{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
            {trend >= 0 ? <TrendingUp size={14} color="rgba(255,255,255,0.8)" /> : <TrendingDown size={14} color="rgba(255,255,255,0.8)" />}
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>vs last month</span>
          </div>
        )}
      </div>
      <div style={{
        width: '48px', height: '48px', borderRadius: '14px',
        background: 'rgba(255,255,255,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={24} color="white" />
      </div>
    </div>
  </div>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [revenueYear, setRevenueYear] = useState(new Date().getFullYear());
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [revenueData, setRevenueData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOrder, setViewOrder] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [negativeBalanceLimit, setNegativeBalanceLimit] = useState(0);

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );

        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );

        setVerificationStatus(
          sellerRes.data.verification_status
        );

        setIsBlocked(
          sellerRes.data.isBlock
        );

        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, []);

  useEffect(() => {
    if (!accessChecked) return;

    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return;
    }

    const fetchDashboard = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );

        const [statsRes, ordersRes, lowStockRes, walletRes] =
          await Promise.all([
            analyticsAPI.getStats(),
            orderAPI.getAll({ limit: 5 }),
            productAPI.getLowStockProducts({
              seller_id: userDetails._id || "",
            }),
            api.get(`/seller-wallet/${userDetails._id}`)
          ]);

        setStats(statsRes.data.stats);
        setRecentOrders(ordersRes.data.orders);
        setLowStockProducts(lowStockRes.data.data || []);
        if (walletRes.data.success) {
          setWalletBalance(walletRes.data.data.wallet.availableBalance || 0);
          if (walletRes.data.data.negativeBalanceLimit !== undefined) {
            setNegativeBalanceLimit(walletRes.data.data.negativeBalanceLimit);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [accessChecked, isBlocked, verificationStatus]);

  useEffect(() => {
    if (!accessChecked || isBlocked || verificationStatus === "P" || verificationStatus === "R") return;

    const fetchRevenue = async () => {
      try {
        const revenueRes = await analyticsAPI.getRevenue(revenueYear);
        setRevenueData(revenueRes.data.data);
      } catch (e) {
        console.error(e);
      }
    };

    fetchRevenue();
  }, [accessChecked, isBlocked, verificationStatus, revenueYear]);

  const statCards = [
    { title: 'Total Products', value: stats?.totalProducts ?? '–', icon: Package, bg: 'linear-gradient(135deg, #667eea, #764ba2)', onClick: () => navigate('/products') },
    { title: 'Total Orders', value: stats?.totalOrders ?? '–', icon: ShoppingCart, bg: 'linear-gradient(135deg, #14b8a6, #0d9488)', onClick: () => navigate('/orders', { state: { status: '' } }) },
    { title: 'Total Revenue', value: stats?.totalAmountSum ?? '–', icon: DollarSign, prefix: '₹', bg: 'linear-gradient(135deg, #10b981, #059669)', onClick: () => navigate('/orders', { state: { status: '' } }) },
    { title: 'Processing Orders', value: stats?.pendingOrders ?? '–', icon: Clock, bg: 'linear-gradient(135deg, #f59e0b, #d97706)', onClick: () => navigate('/orders', { state: { status: 'processing' } }) },
  ];

  const orderStatusColor = {
    pending: '#f59e0b', processing: '#f59e0b', confirmed: '#3b82f6', packed: '#8b5cf6',
    shipped: '#06b6d4', out_for_delivery: '#a855f7', delivered: '#10b981', cancelled: '#64748b',
  };


  if (
    isBlocked ||
    verificationStatus === "P" ||
    verificationStatus === "R"
  ) {
    return (
      <AccessModule
        isBlock={isBlocked}
        verification_status={verificationStatus}
      />
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "300px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #e2e8f0",
              borderTopColor: "#14b8a6",
              borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          <p style={{ color: "#64748b" }}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (viewOrder) {
    return <ViewOrders order={viewOrder} onBack={() => setViewOrder(null)} />;
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{dayjs().format('dddd, MMMM D, YYYY')}</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/products/add')}>
          <Plus size={18} /> Add Product
        </button>
      </div>

      {walletBalance <= negativeBalanceLimit && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #ef4444',
          color: '#b91c1c',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertTriangle size={24} color="#dc2626" />
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Warning: Your limit exceeded to top up</h4>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>Please top up your wallet. Your products are currently not showing on the Decso Cart side.</p>
          </div>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              marginLeft: 'auto',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Topup Now
          </button>
        </div>
      )}

      <style>{`
        .dashboard-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }
        .dashboard-charts-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }
        .dashboard-lists-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
        }
        @media (max-width: 768px) {
          .dashboard-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .dashboard-charts-grid, .dashboard-lists-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Stat Cards */}
      <div className="dashboard-stats-grid">
        {statCards.map((card) => <StatCard key={card.title} {...card} />)}
      </div>

      {/* Charts Row */}
      <div className="dashboard-charts-grid">
        {/* Revenue Chart */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontWeight: '700', color: '#0f172a', fontSize: '16px', margin: 0 }}>Revenue Overview</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                <p style={{ fontSize: '22px', fontWeight: '800', color: '#14b8a6', margin: 0 }}>
                  ₹{revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0).toLocaleString()}
                </p>
                
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 20px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s',
                  color: '#334155', fontWeight: '700', gap: '8px', fontSize: '14px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}
              >
                <span>{revenueYear}</span>
                <ChevronDown size={16} style={{
                  transform: isYearDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: '#475569',
                  strokeWidth: 2.5
                }} />
              </button>

              {isYearDropdownOpen && (
                <>
                  <div onClick={() => setIsYearDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                    background: 'white', border: '1px solid #e2e8f0',
                    borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    padding: '4px 0', zIndex: 50, minWidth: '100px',
                    overflow: 'hidden'
                  }}>
                    {Array.from({ length: new Date().getFullYear() - 2026 + 1 }, (_, i) => 2026 + i).map(year => (
                      <button
                        key={year}
                        onClick={() => {
                          setRevenueYear(year);
                          setIsYearDropdownOpen(false);
                        }}
                        style={{
                          width: '100%', padding: '10px 20px', background: revenueYear === year ? '#2563eb' : 'transparent',
                          color: revenueYear === year ? 'white' : '#475569',
                          border: 'none', textAlign: 'left', cursor: 'pointer',
                          fontSize: '14px', fontWeight: revenueYear === year ? '700' : '400',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (revenueYear !== year) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#334155'; }
                        }}
                        onMouseLeave={(e) => {
                          if (revenueYear !== year) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; }
                        }}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip 
                cursor={{ stroke: '#e2e8f0', strokeWidth: 1, strokeDasharray: '4 4' }}
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div style={{ background: 'white', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: '130px' }}>
                        <div style={{ fontWeight: '700', marginBottom: '8px', color: '#0f172a', fontSize: '13px' }}>{label}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Amount:</span>
                            <span style={{ fontWeight: '700', color: '#14b8a6' }}>₹{data.revenue?.toLocaleString() ?? 0}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Orders:</span>
                            <span style={{ fontWeight: '700', color: '#6366f1' }}>{data.orders ?? 0}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#14b8a6" strokeWidth={2.5} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders Chart */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontWeight: '700', color: '#0f172a', fontSize: '16px', margin: 0 }}>Monthly Orders</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                <p style={{ fontSize: '22px', fontWeight: '800', color: '#667eea', margin: 0 }}>
                  {stats?.totalOrders ?? 0}
                </p>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: '20px' }}>
                  Total Orders
                </span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenueData} barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Bar dataKey="orders" fill="#667eea" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Orders + Low Stock */}
      <div className="dashboard-lists-grid">
        {/* Recent Orders */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#0f172a' }}>Recent Orders</h3>
            <button onClick={() => navigate('/orders')} style={{ color: '#14b8a6', fontSize: '13px', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              View All <ArrowRight size={14} />
            </button>
          </div>
          {recentOrders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
              <ShoppingCart size={32} style={{ margin: '0 auto 12px' }} />
              <p>No orders yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {recentOrders.map((order) => (
                <div key={order._id} onClick={() => setViewOrder(order)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f8fafc', borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s ease' }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>#{order.orderId}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{order.customerName}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px' }}>₹{order.totalAmount?.toLocaleString()}</div>
                    <span className={`badge badge-${order.orderStatus}`} style={{ marginTop: '4px' }}>
                      {order.orderStatus === 'pending' ? 'Processing' : (order.orderStatus ? order.orderStatus.replace(/_/g, ' ').charAt(0).toUpperCase() + order.orderStatus.replace(/_/g, ' ').slice(1) : '')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}  
        </div>

        {/* Low Stock Alert */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#0f172a' }}>Low Stock Alert</h3>
            <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '8px', padding: '2px 8px', fontSize: '12px', fontWeight: '700' }}>
              {lowStockProducts.length}
            </span>
          </div>
          {lowStockProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
              <AlertTriangle size={32} style={{ margin: '0 auto 12px' }} />
              <p style={{ fontSize: '13px' }}>All products well stocked!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '380px', overflowY: 'auto', paddingRight: '4px', }}>
              {lowStockProducts.map((p) => (
                <div key={p._id} onClick={() => navigate('/inventory', { state: { search: p.name } })} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#fff7ed', borderRadius: '8px', borderLeft: '3px solid #f59e0b', cursor: 'pointer', transition: 'background 0.15s ease' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#0f172a' }}>
                    {p.name || 'Unnamed Product'}
                  </div>
                  <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: '12px', padding: '4px 10px', fontSize: '12px', fontWeight: '700' }}>
                    Stock: {p.stock}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
